import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Tag, ShieldCheck, Users, CircleDollarSign, ArrowRight, MapPin, Sparkles } from "lucide-react";
import api from "@/lib/api";
import { ListingCard } from "@/components/ListingCard";
import { FeatureBar } from "@/components/FeatureBar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";

const USPS = [
  { icon: Tag, title: "GRATIS PLAATSEN", sub: "Zonder gedoe", to: "/veilig-handelen#gratis-plaatsen", id: "gratis-plaatsen" },
  { icon: ShieldCheck, title: "EERLIJK & VEILIG", sub: "Jij hebt de controle", to: "/veilig-handelen#eerlijk-veilig", id: "eerlijk-veilig" },
  { icon: Users, title: "ECHTE PARTICULIEREN", sub: "Geen bedrijven, geen ruis", to: "/veilig-handelen#echte-particulieren", id: "echte-particulieren" },
  { icon: CircleDollarSign, title: "GEEN VERBORGEN KOSTEN", sub: "Duidelijk en transparant", to: "/veilig-handelen#geen-verborgen-kosten", id: "geen-verborgen-kosten" },
];

const HERO_IMG = "https://images.unsplash.com/photo-1758613171607-5c0425714354?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function Home() {
  const { isAuthed } = useAuth();
  const [listings, setListings] = useState([]);
  const [personal, setPersonal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const calls = [api.get("/listings?sort=recent&limit=8")];
    if (isAuthed) calls.push(api.get("/listings/personalized"));
    Promise.all(calls).then((res) => {
      setListings(res[0].data.items);
      if (isAuthed && res[1]) setPersonal(res[1].data);
    }).finally(() => setLoading(false));
  }, [isAuthed]);

  return (
    <div>
      <div className="mx-auto max-w-7xl px-4 pb-8 pt-6 md:px-8">
        <section className="vm-fade-up grid grid-cols-1 gap-6 md:grid-cols-12">
          <div className="flex flex-col justify-center md:col-span-7">
            <span className="mb-4 w-fit rounded-full border border-[#333] bg-[#1c1c1c] px-3.5 py-1.5 text-[13px] font-semibold tracking-wide text-[#FF6A00]">KOOP. VERKOOP. SIMPEL.</span>
            <h1 className="font-display text-[2.6rem] leading-[0.98] text-[#F5F5F5] sm:text-6xl lg:text-7xl">
              EENVOUDIG KOPEN.<br /><span className="text-[#FF6A00]">EENVOUDIG VERKOPEN.</span>
            </h1>
            <p className="mt-6 max-w-md text-[17px] leading-relaxed text-[#C2C2C2]">Koop en verkoop de spullen die je niet meer nodig hebt. Snel, veilig en zonder gedoe.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/zoeken" data-testid="hero-browse-btn" className="rounded-full bg-[#FF6A00] px-7 py-3.5 text-[17px] font-bold text-[#111] transition-all duration-200 hover:bg-[#FFB27A] active:scale-95">Bekijk aanbod</Link>
              <Link to="/plaatsen" data-testid="hero-place-btn" className="rounded-full border border-[#444] px-7 py-3.5 text-[17px] font-semibold text-[#F5F5F5] transition-colors hover:border-[#FF6A00] hover:text-[#FF6A00]">Plaats gratis advertentie</Link>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-[#333] md:col-span-5">
            <img src={HERO_IMG} alt="Vrijmarkt community" fetchPriority="high" decoding="async" className="h-64 w-full object-cover md:h-full" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#111] via-transparent to-transparent" />
          </div>
        </section>

        <section className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
          {USPS.map((u) => (
            <Link key={u.title} to={u.to} data-testid={`usp-${u.id}`} aria-label={`${u.title} — meer over veilig en duidelijk handelen`} className="group flex items-start gap-3 rounded-xl border border-[#333] bg-[#1c1c1c] p-4 transition-all duration-200 hover:-translate-y-1 hover:border-[#FF6A00] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF6A00] active:scale-[0.98]">
              <u.icon className="h-6 w-6 shrink-0 text-[#FF6A00]" />
              <div><p className="font-display text-[15px] tracking-wide text-[#F5F5F5] group-hover:text-[#FF6A00]">{u.title}</p><p className="mt-0.5 text-[13px] leading-snug text-[#B4B4B4]">{u.sub}</p></div>
            </Link>
          ))}
        </section>

        <section className="mt-12">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-display text-2xl text-[#F5F5F5] sm:text-3xl">{isAuthed ? "NIEUW" : "UITGELICHT"}</h2>
            <Link to="/zoeken" data-testid="view-all-btn" className="flex items-center gap-1 text-sm font-semibold text-[#FF6A00] hover:underline">Bekijk alles <ArrowRight className="h-4 w-4" /></Link>
          </div>
          {loading ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">{["s1","s2","s3","s4","s5","s6","s7","s8"].map((k) => <div key={k} className="aspect-[4/3] animate-pulse rounded-xl border border-[#333] bg-[#1c1c1c]" />)}</div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">{listings.map((l, i) => <ListingCard key={l.id} listing={l} priority={i < 4} />)}</div>
          )}
        </section>

        <section className="mt-12"><FeatureBar /></section>
      </div>
      <Footer />
    </div>
  );
}

function Section({ title, icon: Icon, items, testid }) {
  return (
    <section className="mt-12" data-testid={testid}>
      <h2 className="mb-4 flex items-center gap-2 font-display text-2xl text-[#F5F5F5] sm:text-3xl"><Icon className="h-6 w-6 text-[#FF6A00]" /> {title}</h2>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">{items.slice(0, 8).map((l, i) => <ListingCard key={l.id} listing={l} priority={i < 4} />)}</div>
    </section>
  );
}
